
import { Star, MapPin, Heart } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCallback, useRef, memo, useEffect, useState } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { useFavorites } from "@/hooks/useFavorites";
import { ServiceImageLoader } from "@/components/ServiceImageLoader";
import { isLowDataMode } from "@/utils/networkMonitor";
import { 
  isDevEnvironment,
  isPreviewEnvironment,
  logImageDetails
} from "@/utils/imageTransformationUtils";
import { useIsMobile } from "@/hooks/use-mobile";
import { DebugTooltip } from "@/components/ui/debug-tooltip";

interface ServiceCardProps {
  id: number;
  title: string;
  subcategory_details: {
    label: string | null;
    parent: {
      label: string | null;
    } | null;
  } | null;
  price: number | null;
  rating: number | null;
  image: string;
  contact: {
    phone: string | null;
    email: string | null;
    location: string | null;
  };
  region: string;
}

// Use memo for the entire component to prevent unnecessary rerenders
export const ServiceCard = memo(({ 
  id, 
  title, 
  subcategory_details, 
  price, 
  rating, 
  image,
  contact,
  region = "nairobi"
}: ServiceCardProps) => {
  const navigate = useNavigate();
  const { isFavorite, addFavorite, removeFavorite, isSyncing, isAuthenticated } = useFavorites();
  // Safely extract category information to prevent React #31 errors
  const categoryLabel = typeof subcategory_details?.parent?.label === 'string' 
    ? subcategory_details.parent.label 
    : 'Uncategorized';
  const subcategoryLabel = typeof subcategory_details?.label === 'string' 
    ? subcategory_details.label 
    : null;
  const displayCategory = subcategoryLabel ? `${categoryLabel} - ${subcategoryLabel}` : categoryLabel;
  const isLowDataRef = useRef(isLowDataMode());
  const isMobile = useIsMobile();
  const isPriority = id <= 3; // First 3 cards are priority
  
  // Use the direct image URL provided
  const [finalImageUrl, setFinalImageUrl] = useState<string>(image || 'https://images.unsplash.com/photo-1486312338219-ce68d2c6f44d');
  const [debugInfo, setDebugInfo] = useState<string>('');
  const [transformId] = useState<string>(`transform-${id}-${Math.random().toString(36).substring(2, 9)}`);

  // Set up debugging info if needed
  useEffect(() => {
    if ((isDevEnvironment() || isPreviewEnvironment()) && image) {
      setDebugInfo(`Original URL: ${image}`);
      logImageDetails(transformId, image, null);
    }
  }, [id, image, transformId]);
  
  const handleCardClick = useCallback(() => {
    navigate(`/service/${id}`);
  }, [id, navigate]);

  const handleFavoriteClick = useCallback(async (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card navigation
    
    if (!isAuthenticated) return;
    
    if (isFavorite(id)) {
      await removeFavorite(id);
    } else {
      await addFavorite(id);
    }
  }, [id, isFavorite, addFavorite, removeFavorite, isAuthenticated]);

  return (
    <Card 
      onClick={handleCardClick}
      data-testid="service-card"
      className="group bg-white rounded-xl overflow-hidden shadow-md hover:shadow-lg transition-shadow duration-300 cursor-pointer h-full flex flex-col"
      style={{
        contain: 'content',
        minHeight: '380px',
      }}
    >
      <div className="relative bg-gray-100 flex-shrink-0" style={{ 
        height: '0',
        paddingBottom: '75%',
        position: 'relative',
        contain: 'strict',
        aspectRatio: '4/3',
      }}>
        {/* Favorite Button Overlay */}
        {isAuthenticated && (
          <Button
            variant="ghost"
            size="icon"
            onClick={handleFavoriteClick}
            disabled={isSyncing}
            className="absolute top-2 right-2 z-10 h-8 w-8 rounded-full bg-white/80 hover:bg-white/90 shadow-sm"
            aria-label={isFavorite(id) ? "Remove from favorites" : "Add to favorites"}
          >
            <Heart 
              className={`h-4 w-4 ${isFavorite(id) ? "fill-red-500 text-red-500" : "text-gray-600"}`} 
            />
          </Button>
        )}
        {(isDevEnvironment() || isPreviewEnvironment()) ? (
          <DebugTooltip
            content={
              <div>
                <p className="font-mono break-all">
                  <span className="text-blue-300">Image URL:</span> {image}
                </p>
                {debugInfo && (
                  <div className="mt-1 pt-1 border-t border-gray-600">
                    <pre className="whitespace-pre-wrap break-all">{debugInfo}</pre>
                  </div>
                )}
              </div>
            }
          >
            <ServiceImageLoader
              src={finalImageUrl}
              alt={title || "Service image"}
              sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
              aspectRatio={4/3}
              priority={isPriority}
              showPlaceholder={!isPriority}
            />
          </DebugTooltip>
        ) : (
          <ServiceImageLoader
            src={finalImageUrl}
            alt={title || "Service image"}
            sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 25vw"
            aspectRatio={4/3}
            priority={isPriority}
            showPlaceholder={!isPriority}
          />
        )}
      </div>
      <CardContent className="p-4 flex-1 flex flex-col" style={{ minHeight: '160px' }}>
        {!isLowDataRef.current && (
          <div 
            className="inline-block px-2 py-0.5 text-xs font-medium text-gray-700 rounded-full mb-1 max-w-full transition-colors duration-300 hover:opacity-90"
            style={{ backgroundColor: 'var(--brand-color-5, #F5FBEF)' }}
          >
            <span className="truncate text-gray-700" style={{ display: 'block' }}>{displayCategory}</span>
          </div>
        )}
        
        <h3 
          className="text-sm font-semibold mb-2" 
          style={{ 
            minHeight: '2.5rem', 
            display: '-webkit-box', 
            WebkitLineClamp: 2, 
            WebkitBoxOrient: 'vertical',
            overflow: 'hidden',
            textOverflow: 'ellipsis'
          }}
        >
          {title || 'Untitled Service'}
        </h3>
        <div className="flex items-center justify-between mt-auto" style={{ minHeight: '1.75rem' }}>
          {price !== null && (
            <div className="text-lg font-bold text-primary truncate max-w-[60%]">
              {region === "nairobi" ? "Ksh" : "USD"} {price}
            </div>
          )}
          {rating !== null && (
            <div className="flex items-center gap-0.5 ml-auto">
              <Star className="w-3 h-3 text-yellow-400 fill-yellow-400 flex-shrink-0" />
              <span className="text-xs font-medium">{rating.toFixed(1)}</span>
            </div>
          )}
        </div>
        
        <div className="space-y-1 text-xs text-gray-600 mt-2" style={{ minHeight: '3rem' }}>
          {contact.location && (
            <div className="flex items-center gap-1.5">
              <MapPin className="w-3 h-3 flex-shrink-0" />
              <span className="truncate">{contact.location}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
});

// Add display name for debugging
ServiceCard.displayName = "ServiceCard";

interface ServiceCardSkeletonProps {
  optimized?: boolean;
}

// Optimize the skeleton to reduce rendering cost
export const ServiceCardSkeleton = memo(({ optimized = false }: ServiceCardSkeletonProps) => {
  const isLowDataModeActive = useRef(isLowDataMode()).current;
  const shouldUseOptimized = isLowDataModeActive || optimized;

  if (shouldUseOptimized) {
    return (
      <Card 
        className="overflow-hidden h-full" 
        style={{ 
          minHeight: '300px',
          contain: 'content',
        }}
      >
        <div className="h-40 bg-gray-200"></div>
        <CardContent className="p-3 space-y-2">
          <div className="h-4 w-full bg-gray-200 rounded my-2"></div>
          <div className="h-4 w-3/4 bg-gray-200 rounded"></div>
          <div className="h-4 w-1/2 bg-gray-200 rounded mt-4"></div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card 
      className="overflow-hidden h-full" 
      style={{ 
        minHeight: '380px',
        contain: 'content',
      }}
    >
      <div 
        className="relative bg-gray-200" 
        style={{ 
          paddingBottom: '75%', 
          position: 'relative',
          aspectRatio: '4/3',
        }}
      > 
        <div className="absolute inset-0 animate-pulse"></div>
      </div>
      <CardContent className="p-3 space-y-2" style={{ minHeight: '160px' }}>
        <div className="h-5 w-1/3 bg-gray-200 animate-pulse rounded-full"></div>
        <div className="h-4 w-full bg-gray-200 animate-pulse rounded" style={{ minHeight: '2.5rem' }}></div>
        <div className="h-4 w-3/4 bg-gray-200 animate-pulse rounded"></div>
        <div className="flex justify-between items-center pt-1" style={{ minHeight: '1.75rem' }}>
          <div className="h-4 w-1/4 bg-gray-200 animate-pulse rounded"></div>
          <div className="h-4 w-1/4 bg-gray-200 animate-pulse rounded"></div>
        </div>
        <div className="space-y-1 pt-1" style={{ minHeight: '3rem' }}>
          <div className="h-3 w-full bg-gray-200 animate-pulse rounded"></div>
        </div>
      </CardContent>
    </Card>
  );
});

// Add display name for debugging
ServiceCardSkeleton.displayName = "ServiceCardSkeleton";
